import { translateBackend } from '@assets/i18n/config';
import type {
  AssetClassLevel,
  FinancialProduct,
} from '@interfaces/financialProduct';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, RefBox } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { Tooltip } from '@ovComponents/2-component/tooltip/tooltip';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberField } from '../numberField/numberField';
import { SecuritySelect } from '../securitySelect/securitySelect';
import type { DragProps, Security, SecurityTableOptions } from './types';
import { getItemStyle } from './types';

const DRAG_HANDLE_MAX_WIDTH = '24px' as const;
const DRAG_HANDLE_COLOR = 'text.secondary' as const;
const GROUPED_MODE_SPACER_WIDTH = '20%' as const;
const TAX_RANK_FONT_FAMILY = 'Interphases Pro' as const;
const TAX_RANK_FONT_SIZE = '14px' as const;
const TAX_RANK_FONT_WEIGHT = 400 as const;
const TAX_RANK_HOVER_COLOR = 'primary.main' as const;
const TARGET_PERCENTAGE_MIN_WIDTH = '100px' as const;
const TARGET_PERCENTAGE_FONT_FAMILY = 'Interphases Pro' as const;
const TARGET_PERCENTAGE_FONT_SIZE = '14px' as const;
const TARGET_PERCENTAGE_FONT_WEIGHT = 400 as const;
const TARGET_PERCENTAGE_DECIMAL_PLACES = 2 as const;
const DRAG_DISABLED_ROW_HEIGHT = '50px' as const;
const DRAG_DISABLED_ROW_WIDTH = '800px' as const;
const TOOLTIP_PLACEMENT = 'right' as const;
const SECURITY_HOVER_COLOR = 'primary.main' as const;

const SecurityCell: FC<{
  security: Security;
  index: number;
  isEditing: boolean;
  dragHandleProps: DragProps['dragHandleProps'];
  onEdit: () => void;
  onUpdate: (security: FinancialProduct) => void;
  editable?: boolean;
  exceptTickers?: string[];
  securitySelectAssetClassLevel?: AssetClassLevel | string;
}> = ({
  security,
  index,
  isEditing,
  dragHandleProps,
  onEdit,
  onUpdate,
  editable = true,
  exceptTickers = [],
  securitySelectAssetClassLevel,
}) => {
  return (
    <TableCell
      testId={`security-cell-${index}`}
      align='left'
      onClick={
        !security.financialProduct.isCash && editable ? onEdit : undefined
      }
    >
      <Box display='flex' alignItems='center' gap={1}>
        {!security.financialProduct.isCash && editable && (
          <Box
            {...dragHandleProps}
            className='hover-element'
            sx={{ maxWidth: DRAG_HANDLE_MAX_WIDTH }}
          >
            <DragIndicatorIcon sx={{ color: DRAG_HANDLE_COLOR }} />
          </Box>
        )}
        {isEditing && !security.financialProduct.isCash && editable ? (
          <RefBox onClick={(e) => e.stopPropagation()} sx={{ flex: 1 }}>
            <SecuritySelect
              testId={`security-select-${index}`}
              value={security.financialProduct.id || ''}
              label=''
              setSecurity={(newSecurity) => {
                if (newSecurity && newSecurity !== 'ANY') onUpdate(newSecurity);
              }}
              exceptTickers={exceptTickers}
              size='small'
              assetClassLevel={securitySelectAssetClassLevel}
            />
          </RefBox>
        ) : (
          <Box
            sx={{
              '&:hover': {
                color:
                  security.financialProduct.isCash || !editable
                    ? 'inherit'
                    : SECURITY_HOVER_COLOR,
              },
            }}
          >
            {(() => {
              const ticker = security?.financialProduct?.ticker;
              const name =
                translateBackend(security?.financialProduct?.translatedName) ||
                security?.financialProduct?.name ||
                '';

              return ticker ? `${ticker} - ${name}` : name;
            })()}
          </Box>
        )}
      </Box>
    </TableCell>
  );
};

export type SecurityRowProps = {
  security: Security;
  index: number;
  isGroupedMode: boolean;
  editable: boolean;
  exceptTickers: string[];
  options?: SecurityTableOptions;
  dragProps?: DragProps;
  securityEditingIndex: number | null;
  onEditSecurity: (index: number) => void;
  onUpdateSecurity: (index: number, newSecurity: FinancialProduct) => void;
  taxRankEditingIndex: number | null;
  currentTaxRankValue: string;
  selectTaxRankRef: React.RefObject<HTMLDivElement>;
  onTaxRankFocus: (index: number) => void;
  onTaxRankBlur: (index: number) => void;
  onTaxRankKeyDown: (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  onTaxRankValueChange: (value: string) => void;
  targetPercentageEditingIndex: number | null;
  securitiesPercentage: string[];
  selectTargetPercentageRef: React.RefObject<HTMLDivElement>;
  onTargetPercentageFocus: (index: number) => void;
  onTargetPercentageKeyDown: (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  onPercentageChange: (index: number, value: string) => void;
  onRemoveSecurity?: (index: number) => void;
  onReorderSecurities?: (securities: Security[]) => void;
  initialSecurities: Security[];
  securitySelectAssetClassLevel?: AssetClassLevel | string;
};

export const SecurityRow: FC<SecurityRowProps> = ({
  security,
  index,
  isGroupedMode,
  editable,
  exceptTickers,
  options,
  dragProps,
  securityEditingIndex,
  onEditSecurity,
  onUpdateSecurity,
  taxRankEditingIndex,
  currentTaxRankValue,
  selectTaxRankRef,
  onTaxRankFocus,
  onTaxRankBlur,
  onTaxRankKeyDown,
  onTaxRankValueChange,
  targetPercentageEditingIndex,
  securitiesPercentage,
  selectTargetPercentageRef,
  onTargetPercentageFocus,
  onTargetPercentageKeyDown,
  onPercentageChange,
  onRemoveSecurity,
  onReorderSecurities,
  initialSecurities,
  securitySelectAssetClassLevel,
}) => {
  const { t } = useTranslation(['modelPortfolios']);
  const assetClassLabel =
    translateBackend(
      security.financialProduct.primaryAssetClass?.translatedName,
    ) || security.financialProduct.primaryAssetClass?.name;
  const getTaxRankDisplay = (
    isCash?: boolean,
    isGrouped?: boolean,
  ): string | number => {
    if (isCash) return '';
    return isGrouped ? security?.taxRank || '' : index + 1;
  };

  return (
    <TableRow
      ref={dragProps?.innerRef}
      {...(dragProps?.draggableProps ?? {})}
      {...(dragProps?.rowHandleProps ?? {})}
      style={
        dragProps
          ? getItemStyle(dragProps.isDragging, dragProps.draggableProps.style)
          : { height: DRAG_DISABLED_ROW_HEIGHT, width: DRAG_DISABLED_ROW_WIDTH }
      }
      showHoverElements
      hover
    >
      {isGroupedMode && <TableCell sx={{ width: GROUPED_MODE_SPACER_WIDTH }} />}
      <SecurityCell
        security={security}
        index={index}
        isEditing={securityEditingIndex === index}
        dragHandleProps={dragProps?.dragHandleProps ?? undefined}
        onEdit={() => onEditSecurity(index)}
        onUpdate={(newSecurity: FinancialProduct) =>
          onUpdateSecurity(index, newSecurity)
        }
        editable={editable}
        exceptTickers={exceptTickers}
        securitySelectAssetClassLevel={securitySelectAssetClassLevel}
      />
      {!isGroupedMode && (
        <TableCell align='center'>
          {assetClassLabel && (
            <Badge
              data-testid={`security-asset-class-badge-${index}`}
              label={assetClassLabel}
              color={security.color}
            />
          )}
        </TableCell>
      )}
      {/* Tax rank: user-editable only in ASSET_CLASS grouped mode */}
      <TableCell
        align='center'
        onClick={() =>
          isGroupedMode &&
          !security.financialProduct.isCash &&
          editable &&
          onTaxRankFocus(index)
        }
      >
        {isGroupedMode &&
        taxRankEditingIndex === index &&
        !security.financialProduct.isCash &&
        editable ? (
          <RefBox ref={selectTaxRankRef} onClick={(e) => e.stopPropagation()}>
            <NumberField
              testId={`tax-rank-input-${index}`}
              number={currentTaxRankValue}
              setNumber={onTaxRankValueChange}
              onBlur={() => onTaxRankBlur(index)}
              InputProps={{
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                  onTaxRankKeyDown(index, e),
                onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                  e.target.select(),
              }}
              label=''
              sx={{ width: '100%' }}
              showArrows={false}
              size='small'
              unsigned
            />
          </RefBox>
        ) : (
          <Box
            data-testid={`security-tax-rank-${index}`}
            sx={{
              '&:hover': {
                color:
                  security.financialProduct.isCash ||
                  !editable ||
                  !isGroupedMode
                    ? 'inherit'
                    : TAX_RANK_HOVER_COLOR,
              },
              textAlign: 'left',
              fontFamily: TAX_RANK_FONT_FAMILY,
              fontSize: TAX_RANK_FONT_SIZE,
              fontWeight: TAX_RANK_FONT_WEIGHT,
            }}
          >
            {getTaxRankDisplay(security.financialProduct.isCash, isGroupedMode)}
          </Box>
        )}
      </TableCell>
      <TableCell
        align='center'
        onClick={() => editable && onTargetPercentageFocus(index)}
        sx={{ minWidth: TARGET_PERCENTAGE_MIN_WIDTH }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            {targetPercentageEditingIndex === index && editable ? (
              <RefBox
                ref={selectTargetPercentageRef}
                onClick={(e) => e.stopPropagation()}
              >
                <NumberField
                  testId={`target-percentage-input-${index}`}
                  number={securitiesPercentage[index]}
                  setNumber={(value) => {
                    onPercentageChange(index, value);
                  }}
                  InputProps={{
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                      onTargetPercentageKeyDown(index, e),
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.select(),
                  }}
                  decimalPlaces={TARGET_PERCENTAGE_DECIMAL_PLACES}
                  label=''
                  trailingIcon='percent'
                  showArrows={false}
                  size='small'
                  unsigned
                />
              </RefBox>
            ) : (
              <Box
                data-testid={`security-target-percentage-${index}`}
                sx={{
                  '&:hover': {
                    color: editable ? 'primary.main' : 'inherit',
                  },
                  textAlign: 'right',
                  fontFamily: TARGET_PERCENTAGE_FONT_FAMILY,
                  fontSize: TARGET_PERCENTAGE_FONT_SIZE,
                  fontWeight: TARGET_PERCENTAGE_FONT_WEIGHT,
                }}
              >
                {security.percentage?.toFixed(2) || ''}
              </Box>
            )}
          </Box>
          <Box sx={{ p: 0.5 }}>
            {options?.securityAllocationChart?.columns?.[0]
              ?.buttonRemoveSecurity?.enabled &&
              onRemoveSecurity &&
              targetPercentageEditingIndex !== index &&
              editable &&
              (security.isCashEquivalent ? (
                <Tooltip
                  data-testid={`remove-security-button-${index}`}
                  placement={TOOLTIP_PLACEMENT}
                  title={
                    <Typography sx={{ color: 'white' }}>
                      {t('modelPortfolios:cashEquivalent')}
                    </Typography>
                  }
                >
                  <IconButton
                    data-testid={`remove-security-button-${index}`}
                    className='hover-element'
                    size='small'
                    disabled
                  >
                    <RemoveCircleIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip
                  data-testid={`remove-security-button-${index}`}
                  placement={TOOLTIP_PLACEMENT}
                  title={
                    <Typography sx={{ color: 'white' }}>
                      {t('modelPortfolios:remove')}
                    </Typography>
                  }
                >
                  <IconButton
                    data-testid={`remove-security-button-${index}`}
                    className='hover-element'
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveSecurity(index);
                      onReorderSecurities?.(
                        initialSecurities.filter((_, i) => i !== index),
                      );
                    }}
                    size='small'
                  >
                    <RemoveCircleOutlineIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              ))}
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
};
